import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ToastService } from '../../core';
import { PatientTableComponent } from '../../list';
import { ChartItemEnum, DashboardService } from '../../services';
import { BaseComponent, PaginationComponent } from '../../shared';
import { PieChartComponent } from '../pie-chart';


/**
 * Component for device status in dashboard page.
 */
@Component({
	selector: 'tsp-dashboard-device-status',
	imports: [
		PieChartComponent,
		PaginationComponent,
		PatientTableComponent
	],
	templateUrl: './dashboard-device-status.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class DashboardDeviceStatusComponent extends BaseComponent {

	private readonly srv = inject(DashboardService);
	private readonly toastSrv = inject(ToastService);

	selectedChart = computed(() => this.srv.selectedChart());
	items = computed(() => this.srv.items());
	collectionSize = computed(() => this.srv.itemsSize());

	chartsTitle = computed(() => this.labels().DASHBOARD_COMPONENT_TITLE_APP_STATUS);
	isChartsVisible = computed(() => !!this.srv.appStatus());

	activationValue = computed(() => this.srv.appActivations());
	activationLegend = [this.labels().DASHBOARD_COMPONENT_LEGEND_ACTIVATION, this.labels().DASHBOARD_COMPONENT_LEGEND_ACTIVATION_NOT];
	activationChartId = 'app-activation';
	activationChartTitle: string = this.labels().DASHBOARD_COMPONENT_TITLE_ACTIVATION;

	syncValue = computed(() => this.srv.appSyncs());
	syncLegend = [this.labels().DASHBOARD_COMPONENT_LEGEND_SYNC, this.labels().DASHBOARD_COMPONENT_LEGEND_SYNC_NOT];
	syncChartId = 'prescription-sync';
	syncChartTitle: string = this.labels().DASHBOARD_COMPONENT_TITLE_SYNC;

	scheduleValue = computed(() => this.srv.appAdjustments());
	scheduleLegend = [this.labels().DASHBOARD_COMPONENT_LEGEND_SCHEDULE, this.labels().DASHBOARD_COMPONENT_LEGEND_SCHEDULE_NOT];
	scheduleChartId = 'struts-adjustment';
	scheduleChartTitle: string = this.labels().DASHBOARD_COMPONENT_TITLE_SCHEDULE;

	tableTitle = computed(() => this._tableTitle);

	chartItems = ChartItemEnum;
	pageNumber: number = 1;
	pageSize: number = 10;


	private get _tableTitle(): string {
		switch (this.selectedChart()) {
			case ChartItemEnum.ACTIVATED: return `${this.activationChartTitle?.toUpperCase()} (${this.activationLegend[0]})`;
			case ChartItemEnum.ACTIVATED_NOT: return `${this.activationChartTitle?.toUpperCase()} (${this.activationLegend[1]})`;
			case ChartItemEnum.SYNC: return `${this.syncChartTitle?.toUpperCase()} (${this.syncLegend[0]})`;
			case ChartItemEnum.SYNC_NOT: return `${this.syncChartTitle?.toUpperCase()} (${this.syncLegend[1]})`;
			case ChartItemEnum.SCHEDULE: return `${this.scheduleChartTitle?.toUpperCase()} (${this.scheduleLegend[0]})`;
			case ChartItemEnum.SCHEDULE_NOT: return `${this.scheduleChartTitle?.toUpperCase()} (${this.scheduleLegend[1]})`;
			default: return '';
		}
	}

	/**
	 * Update selected chart item.
	 */
	select(chart: ChartItemEnum): void {
		this.srv.selectedChart.set(chart);
		this._update();
	}

	/**
	 * Update list by page number.
	 */
	updatePageNumber(): void {
		this.srv.filters.update(f => ({ ...f, page: this.pageNumber }));
		this._update();
	}

	/**
	 * Update list by page size.
	 */
	updatePageSize(): void {
		this.srv.filters.update(f => ({ ...f, top: this.pageSize }));
		this._update();
	}

	private _update(): void {
		if (!this.collectionSize() || Math.ceil(this.collectionSize() / this.pageSize) >= this.pageNumber) {
			this.srv.updateList().subscribe({
				error: () => this.toastSrv.showError(this.labels().DASHBOARD_COMPONENT_SEARCH_ERROR)
			});
		}
	}

}
